import React, { useState, useEffect, Suspense } from 'react';
import { lazyWithRetry as lazy } from './utils/lazyWithRetry';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { motion, AnimatePresence, LazyMotion, domAnimation } from 'framer-motion';
import axios from 'axios';
import Lenis from 'lenis';
import CustomCursor from './Components/Cursor';
import Loading from './Components/Loading/Loading';
import PullToRefresh from './Components/Common/PullToRefresh';
import HapticProvider from './Components/Common/HapticProvider';
import config from './config';
import Home from './Home';
// import SinglePage from './SinglePage';
import BackToTop from './Components/Common/BackToTop';

const Login = lazy(() => import('./Components/Login/Login'));
const Clients = lazy(() => import('./Components/Clients/Clients'));
const ClientPublicPage = lazy(() => import('./Components/Clients/ClientPublicPage'));
const AdminHome = lazy(() => import('./Components/Admin/AdminHome'));
const AdminProjects = lazy(() => import('./Components/Admin/AdminProjects'));
const AdminSkills = lazy(() => import('./Components/Admin/AdminSkills'));
const AdminResume = lazy(() => import('./Components/Admin/AdminResume'));
const AdminTestimonials = lazy(() => import('./Components/Admin/AdminTestimonials'));
const AdminChatbot = lazy(() => import('./Components/Admin/AdminChatbot'));
const AdminClients = lazy(() => import('./Components/Admin/AdminClients'));
const AdminCertifications = lazy(() => import('./Components/Admin/AdminCertifications'));
const AdminContacts = lazy(() => import('./Components/Admin/AdminContacts'));
const AdminProfile = lazy(() => import('./Components/Admin/AdminProfile'));
const AdminAbout = lazy(() => import('./Components/Admin/AdminAbout'));
const AdminSettings = lazy(() => import('./Components/Admin/AdminSettings'));
const Maintenance = lazy(() => import('./Components/Common/Maintenance'));
const TestimonialPublic = lazy(() => import('./Components/Testimonials/TestimonialPublic'));
const EditSkillOrProject = lazy(() => import('./Components/Admin/EditSkillOrProject'));
const ProtectedRoute = lazy(() => import('./Components/Admin/ProtectedRoute'));

const PageTransition = React.memo(({ children }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.3 }}
  >
    <Suspense fallback={<ComponentLoader />}>
      {children}
    </Suspense>
  </motion.div>
));

PageTransition.displayName = 'PageTransition';

const ComponentLoader = () => {
  return <Loading />;
};

const AdminPage = React.memo(({ children }) => (
  <div className="admin-page-wrapper">
    <div className="admin-content-wrapper">
      {children}
    </div>
  </div>
));
AdminPage.displayName = 'AdminPage';



const AppContent = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // Temporarily disable Lenis to debug scroll issue
    /*
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: 'vertical',
      gestureOrientation: 'vertical',
      smoothWheel: true,
      wheelMultiplier: 1,
      smoothTouch: false,
      touchMultiplier: 2,
      infinite: false,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
    */
  }, []);

  const apiEndpoints = React.useMemo(() => ({
    skills: `${config.apiUrl}/skills`,
    projects: `${config.apiUrl}/projects`,
    testimonials: `${config.apiUrl}/testimonials`
  }), []);

  const adminRoutes = React.useMemo(() => [
    { path: "/AdminHome", component: AdminHome },
    { path: "/AdminProjects", component: AdminProjects },
    { path: "/AdminSkills", component: AdminSkills },
    { path: "/AdminResume", component: AdminResume },
    { path: "/AdminTestimonials", component: AdminTestimonials },
    { path: "/AdminChatbot", component: AdminChatbot },
    { path: "/AdminClients", component: AdminClients },
    { path: "/AdminCertifications", component: AdminCertifications },
    { path: "/AdminContacts", component: AdminContacts },
    { path: "/AdminProfile", component: AdminProfile },
    { path: "/AdminAbout", component: AdminAbout },
    { path: "/AdminSettings", component: AdminSettings },
    { path: "/edit-skill-or-project", component: EditSkillOrProject },
    { path: "/edit-skill-or-project/:type/:id", component: EditSkillOrProject }
  ], []);

  useEffect(() => {
    let isMounted = true;

    const preloadAll = async () => {
      try {
        // Check Maintenance Mode first
        const settingsRes = await axios.get(`${config.apiUrl}/settings`).catch(() => null);
        if (settingsRes?.data?.success) {
          setMaintenanceMode(settingsRes.data.settings.maintenanceMode);
        }

        const apiPreloads = Object.values(apiEndpoints).map(url =>
          axios.get(url).catch(console.error)
        );

        await Promise.all(apiPreloads);

        if (isMounted) {
          setTimeout(() => setIsLoading(false), 500);
        }
      } catch (error) {
        console.error('Error during preloading:', error);
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    preloadAll();

    return () => {
      isMounted = false;
    };
  }, [apiEndpoints]);

  const routes = React.useMemo(() => {
    // Check if user is an admin
    const user = JSON.parse(localStorage.getItem('user'));
    const isAdmin = user && (user.role === 'admin' || user.id === 'admin');
    const isAdminPath = location.pathname.toLowerCase().startsWith('/admin');

    if (maintenanceMode && !isAdmin && !isAdminPath && location.pathname !== '/login') {
      return (
        <Routes>
          <Route path="*" element={<PageTransition><Maintenance /></PageTransition>} />
          <Route path="/login" element={<PageTransition><Login /></PageTransition>} />
        </Routes>
      );
    }

    return (
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<Home name="Qadirdad Kazi" />} />
        <Route path="/login" element={<PageTransition><Login /></PageTransition>} />
        <Route path="/testimonial" element={<PageTransition><TestimonialPublic /></PageTransition>} />
        <Route
          path="/clients"
          element={
            <PageTransition>
              <Clients />
            </PageTransition>
          }
        />
        <Route
          path="/clients/:id"
          element={
            <PageTransition>
              <ClientPublicPage />
            </PageTransition>
          }
        />
        {adminRoutes.map(({ path, component: Component }) => (
          <Route
            key={path}
            path={path}
            element={
              <PageTransition>
                <ProtectedRoute>
                  <AdminPage><Component /></AdminPage>
                </ProtectedRoute>
              </PageTransition>
            }
          />
        ))}
      </Routes>
    );
  }, [location, adminRoutes, maintenanceMode]);

  if (isLoading) {
    return <Loading />;
  }

  const handleRefresh = async () => {
    // Custom refresh logic
    try {
      // Re-fetch data or reload specific components
      const apiPreloads = Object.values(apiEndpoints).map(url =>
        axios.get(url).catch(console.error)
      );
      await Promise.all(apiPreloads);

      // Optional: Show success feedback
      console.log('Content refreshed successfully!');
    } catch (error) {
      console.error('Refresh failed:', error);
    }
  };

  return (
    <HapticProvider>
      <CustomCursor />
      <PullToRefresh onRefresh={handleRefresh} />
      <BackToTop />
      <main className="main-content">
        <LazyMotion features={domAnimation}>
          <AnimatePresence mode="wait">
            {routes}
          </AnimatePresence>
        </LazyMotion>
      </main>
    </HapticProvider>
  );
};

function App() {
  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <AppContent />
    </Router>
  );
}

export default App;
